<template>
    <v-card flat>
        <v-card-title primary-title class="justify-center">
            <div class="pb-3 headline font-weight-medium">{{ $t('registerTitle') }}</div>
        </v-card-title>

        <v-card-subtitle class="text-center pb-0">
            {{ $t('registerMessage') }}
        </v-card-subtitle>

        <v-card-text>
            <v-container>
                <v-form ref="form" v-model="valid">
                    <v-row class="px-3">
                        <v-col cols="12">
                            <v-text-field
                                outlined
                                v-model.trim="username"
                                hide-details="auto"
                                :rules="usernameRules"
                                :label="$t('registerUsername')"
                                autocomplete="username"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                outlined
                                v-model.trim="email"
                                hide-details="auto"
                                :label="$t('registerEmail')"
                                :rules="emailRules"
                                type="email"
                                inputmode="email"
                                autocomplete="email"
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                outlined
                                hide-details="auto"
                                v-model="password"
                                :label="$t('registerPassword')"
                                :rules="passwordRules"
                                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="showPassword ? 'text' : 'password'"
                                @click:append="showPassword = !showPassword"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                outlined
                                hide-details="auto"
                                v-model="passwordConfirm"
                                :label="$t('registerPasswordConfirm')"
                                :rules="confirmPasswordRules"
                                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="showPassword ? 'text' : 'password'"
                                @click:append="showPassword = !showPassword"
                            />
                        </v-col>

                        <v-col cols="12" class="py-0 text-center">
                            <a :href="config.PRIVACY_URL" target="_blank" rel="noopener noreferrer">
                                {{ $t('privacyPolicy') }}
                            </a>
                        </v-col>

                        <v-col cols="12" class="py-0 text-center">
                            <a :href="config.TERMS_URL" target="_blank" rel="noopener noreferrer">
                                {{ $t('termsAndConditions') }}
                            </a>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <v-switch
                                class="mt-0"
                                :color="theme.btnColorPrimary"
                                v-model="acceptPrivacyPolicy"
                                :label="$t('privacyPolicy')"
                                :rules="privacyPolicyRules"
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <v-switch
                                class="mt-0"
                                :color="theme.btnColorPrimary"
                                v-model="acceptTerms"
                                :label="$t('termsAndConditions')"
                                :rules="termsRules"
                                hide-details="auto"
                            />
                        </v-col>
                    </v-row>

                    <v-row align="center" justify="center" class="ma-0 mt-3">
                        <v-col cols="12" md="6" class="text-center">
                            <v-btn
                                large
                                depressed
                                block
                                color="grey lighten-1"
                                dark
                                rounded
                                :disabled="loading"
                                @click="$emit('change')"
                            >
                                {{ $t('back') }}
                            </v-btn>
                        </v-col>

                        <v-col cols="12" md="6" class="text-center">
                            <v-btn
                                large
                                depressed
                                block
                                :color="theme.btnColorPrimary"
                                rounded
                                dark
                                :loading="loading"
                                :disabled="!valid || loading"
                                @click="registerUser"
                            >
                                {{ $t('registerBtn') }}
                            </v-btn>
                            <div v-if="register_error" class="red--text mt-1"
                                 v-html="$t(register_error)">
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
            </v-container>
        </v-card-text>

        <HLConfirm
            v-model="showDialog"
            title=""
            :text="$t('registerMsgOk')"
            @success="confirmRegistration"
        />
    </v-card>
</template>

<script>
import CONFIG from "../../constants/config";
import { layoutMixin } from "../../utils/layout-mixin";
import { settingsMixin } from "../../utils/settings-mixin";
import { validationMixin } from "../../utils/validation-mixin";
import HLConfirm from "../ui/HLConfirm";
import sha1 from "js-sha1";

export default {
    name: "RegisterForm",
    mixins: [settingsMixin, validationMixin, layoutMixin],
    components: { HLConfirm },

    props: {
        user: {
            type: Object,
            default: null
        }
    },

    data: () => ({
        config: CONFIG,
        username: "",
        email: "",
        password: "",
        passwordConfirm: "",
        showPassword: false,
        acceptPrivacyPolicy: false,
        acceptTerms: false,
        register_error: "",
        valid: false,
        loading: false,
        showDialog: false
    }),
    computed: {
        registerData() {
            return {
                id: null,
                username: this.username,
                email: this.email,
                password: sha1(this.password + this.email),
                confirm_password: sha1(this.passwordConfirm + this.email),
            };
        },

        // If your validation mixin already defines these, remove the blocks below.
        confirmPasswordRules() {
            return [
                v => !!v || this.$t("Password is required"),
                v => v === this.password || this.$t("Passwords must match")
            ];
        },
    },

    methods: {
        validate() {
            return this.$refs.form && this.$refs.form.validate();
        },

        async registerUser() {
            this.register_error = "";
            const ok = this.validate();
            if (!ok) return;

            this.loading = true;
            try {
                await this.register(this.registerData);
                this.showDialog = true;
            } catch (ex) {
                let msg = "";
                if (ex && ex.response && ex.response.data && ex.response.data.errors) {
                    Object.entries(ex.response.data.errors).forEach(([field, key]) => {
                        console.log("Register Error", key);
                        if (key) {
                            msg = "errorCode" + this.capitalizeFirstLetter(key);
                        }
                    });
                } else {
                    msg = "errorCodeInvalidCredentials";
                }
                this.register_error = msg;
            } finally {
                this.loading = false;
            }
        },

        confirmRegistration() {
            this.showDialog = false;
            this.register_error = "";
            this.$emit("next");
        },
        capitalizeFirstLetter(val) {
            return String(val).charAt(0).toUpperCase() + String(val).slice(1);
        },
    }
};
</script>